import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { GIButton, Modal } from 'app-components';
import { useDispatch, useSelector } from 'react-redux';
import Select, { MultiValue } from 'react-select';

import { useApiHandler } from '../hooks/useApiHandler';
import { SelectIcon, SelectWithIconWrapper, StyledModal } from '../constants/ticketlist.styled';
import { IAddWatchersModal } from '../constants/ticketlist.interface';
import { useDebounce } from '@containers/common/useDebounce';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { AUTOCOMPLETE_DEBOUNCE_DELAY, AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH } from '../constants/ticketlist.constants';
import { refreshTicketList } from '../constants/ticketlist.utils';

export const AddWatchersModal = ({ onClose, selectedRows, resetSelection }: IAddWatchersModal) => {
    const dispatch = useDispatch();
    const { updateWatchersApi, findWacthers, defaultListAPI, listAPI, filteredListAPI } = useApiHandler();
    const [selectedOptions, setSelectedOptions] = useState<MultiValue<never>>([]);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const { listApiType } = useSelector((state: any) => state.ticketMgmt.ticketsList) || {};

    const debounceSearchValue = useDebounce(inputValue, AUTOCOMPLETE_DEBOUNCE_DELAY);

    const onSubmit = async () => {
        const payload = {
            ticketIds: selectedRows,
            userIds: selectedOptions.map((option: { value: number }) => option.value),
            watch: true,
        };
        await updateWatchersApi(dispatch, payload);
        refreshTicketList({
            dispatch,
            listApiType,
            defaultListAPI,
            listAPI,
            filteredListAPI,
        });
        resetSelection();
        onClose();
    };

    const onSelect = (selected: MultiValue<never>) => {
        setInputValue('');
        setSelectedOptions(selected);
    };

    const loadWatchers = useCallback(
        async (searchTerm: string) => {
            const { watcherDTOList } = await findWacthers(dispatch, {
                userText: searchTerm,
            });

            const watchers = watcherDTOList.map(user => ({
                value: user.userId,
                label: `${user.firstName} ${user.lastName}`,
            }));

            setOptions(watchers);
            return watchers;
        },
        [dispatch, findWacthers],
    );

    useEffect(() => {
        if (!debounceSearchValue.trim() || debounceSearchValue.trim().length <= AUTOCOMPLETE_MIN_SEARCH_QUERY_LENGTH) {
            setOptions([]);
            return;
        }
        loadWatchers(debounceSearchValue);
    }, [debounceSearchValue]);

    return (
        <StyledModal>
            <Modal id="addWatchersModal" data-testid="add-watchers-modal">
                <Modal.Header
                    id="addWatchersModalHeader"
                    titleId="ticketList.modal.addWatchers.header"
                    data-testid="add-watchers-modal-header"
                    onHide={onClose}
                />
                <Modal.Body data-testid="add-watchers-modal-body">
                    <SelectWithIconWrapper>
                        <SelectIcon icon={faSearch} />
                        <Select
                            id="addWatchersSelect"
                            className="add-watchers-select"
                            classNamePrefix="select"
                            isMulti
                            options={options}
                            inputValue={inputValue}
                            menuIsOpen={Boolean(inputValue)}
                            onInputChange={(newValue, action) => {
                                if (action.action === 'input-change') {
                                    setInputValue(newValue);
                                }
                            }}
                            onChange={value => {
                                onSelect(value);
                            }}
                            components={{
                                DropdownIndicator: () => null,
                                IndicatorSeparator: () => null,
                            }}
                            filterOption={() => true}
                            placeholder={<FormattedMessage id="ticketList.modal.addWatchers.addUsers.placeholder" />}
                        />
                    </SelectWithIconWrapper>
                </Modal.Body>
                <Modal.Controls data-testid="add-watchers-modal-controls">
                    <GIButton id="addWatchersButton" onClickHandler={onSubmit} variant="primary" labelId="button.add" />
                </Modal.Controls>
            </Modal>
        </StyledModal>
    );
};
